import React from 'react';
import { Tooltip } from 'antd';
import { LinkOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { PromotionSchedule, PromotionScheduleWeekMeta } from '../../../../../lib/api/promotionSchedule';
import { GRAY_BAR_TONE, STATUS_STYLE, TRACK_META } from '../../constants';
import {
  DAY_TYPE_COLOR,
  WeekSegment,
  formatCouponBenefit,
  getDayType,
  getTimeRangeLabel,
  getWeekDates,
  sortCouponsByBenefit,
} from '../../utils';
import { ScheduleTooltip } from '../ScheduleTooltip';
import { StyledCalendarBar, StyledDayNumber, StyledDayNumberRow, StyledLaneGrid, StyledWeekRow } from './styles';

type Props = {
  week: PromotionScheduleWeekMeta;
  laneRows: WeekSegment[][];
  month: number;
  today: string;
  holidayMap: Map<string, string | null>;
  holidayDates: Set<string>;
  /** 이 주에 걸친 항목이 hover 중일 때만 id, 아니면 null — 덕분에 다른 주는 리렌더되지 않는다 */
  hoveredScheduleId: number | null;
  onHoverChange: (scheduleId: number | null) => void;
  onClickSchedule: (schedule: PromotionSchedule) => void;
};

/**
 * 캘린더 한 주.
 *
 * **React.memo 로 감싼 이유** — hover 강조는 여러 주에 걸친 항목 전부를 칠해야 해서
 * 상태를 루트에 둘 수밖에 없다. 그대로 두면 마우스가 움직일 때마다 연 단위 조회의
 * 막대 조각 1000여 개가 전부 다시 그려진다. 각 주는 **자기 주에 hover 항목이 있을 때만**
 * `hoveredScheduleId` 를 받으므로(아니면 null), 나머지 주는 props 가 그대로라 건너뛴다.
 *
 * hover 를 상태로 들고 있는 이유(= DOM 클래스 방식이 왜 안 되는지)는
 * `CalendarView` 의 `hoveredScheduleId` 선언부에 적어 뒀다.
 */
const CalendarWeekRowBase = (props: Props): React.JSX.Element => {
  const { week, laneRows, month, today, holidayMap, holidayDates, hoveredScheduleId, onHoverChange, onClickSchedule } =
    props;

  const weekDates = getWeekDates(week.startDate);

  return (
    <StyledWeekRow>
      <StyledDayNumberRow>
        {weekDates.map((date) => {
          const holidayMemo = holidayMap.get(date);
          const dayTypeColor = DAY_TYPE_COLOR[getDayType(date, holidayDates)];

          return (
            <StyledDayNumber
              key={date}
              $isOtherMonth={dayjs(date).month() + 1 !== month}
              $isToday={date === today}
              $color={dayTypeColor.dayNumberText}
              $background={dayTypeColor.background}
            >
              <span className="day-number">{dayjs(date).date()}</span>
              {holidayMemo && (
                <span className="holiday-memo" title={holidayMemo}>
                  {holidayMemo}
                </span>
              )}
            </StyledDayNumber>
          );
        })}
      </StyledDayNumberRow>

      <StyledLaneGrid>
        {laneRows.map((laneRow, laneIndex) =>
          laneRow.map((segment) => {
            const { schedule, isClippedStart, isClippedEnd } = segment;
            const statusStyle = STATUS_STYLE[schedule.status];
            const trackMeta = TRACK_META[schedule.track];
            // 시간 지정 항목만 시각이 붙는다 (종일이면 null)
            const timeRangeLabel = getTimeRangeLabel(schedule.startDate, schedule.endDate);
            // 혜택 큰 순 — 간트와 같은 규약 (서버는 id 순이라 작은 혜택이 앞에 온다)
            const sortedCoupons = sortCouponsByBenefit(schedule.coupons ?? []);

            return (
              <Tooltip key={schedule.id} title={<ScheduleTooltip schedule={schedule} />}>
                <StyledCalendarBar
                  type="button"
                  $startColumn={segment.startColumn + 1}
                  $span={segment.span}
                  $row={laneIndex + 1}
                  $tone={statusStyle.isGray ? GRAY_BAR_TONE : trackMeta.barTone}
                  $opacity={statusStyle.opacity}
                  $isClippedStart={isClippedStart}
                  $isClippedEnd={isClippedEnd}
                  $isHovered={hoveredScheduleId === schedule.id}
                  onClick={() => onClickSchedule(schedule)}
                  onMouseEnter={() => onHoverChange(schedule.id)}
                  onMouseLeave={() => onHoverChange(null)}
                >
                  {isClippedStart && <span className="clip-mark">◀</span>}
                  {timeRangeLabel && <span className="bar-time">{timeRangeLabel}</span>}
                  {/*
                    담당자·쿠폰은 **세그먼트가 넓을 때만** 붙인다.
                    캘린더 한 칸은 간트 하루보다 훨씬 넓어서, 2~3일만 이어져도
                    제목 말고 더 담을 수 있다. 좁은 칸에서는 제목이 우선이다.
                  */}
                  {segment.span >= 2 && schedule.adminName && <span className="bar-manager">{schedule.adminName}</span>}
                  <span className="bar-title">{schedule.title}</span>
                  {/*
                    연결 표식은 **제목 바로 뒤**다 — 간트와 같은 규약이자 위치.
                    ⚠️ 뒤쪽(쿠폰 칩 다음)에 두면 막대가 `overflow: hidden` 이라
                    제목이 조금만 길어도 잘려 사라진다. 제목은 줄어들 수 있지만
                    표식은 줄어들지 않으므로 앞에 둬야 살아남는다.
                  */}
                  {schedule.event && <LinkOutlined className="bar-flag bar-flag-event" title="기획전 연결됨" />}
                  {sortedCoupons.length > 0 && (
                    <span className="bar-flag bar-flag-coupon" title={`쿠폰 ${sortedCoupons.length}개`} />
                  )}
                  {segment.span >= 3 &&
                    sortedCoupons.slice(0, 2).map((coupon) => (
                      <span key={coupon.id} className="bar-chip" title={coupon.name}>
                        {formatCouponBenefit(coupon)}
                      </span>
                    ))}
                  {segment.span >= 3 && sortedCoupons.length > 2 && (
                    <span className="bar-chip-more">+{sortedCoupons.length - 2}</span>
                  )}
                  {isClippedEnd && <span className="clip-mark">▶</span>}
                </StyledCalendarBar>
              </Tooltip>
            );
          }),
        )}
      </StyledLaneGrid>
    </StyledWeekRow>
  );
};

export const CalendarWeekRow = React.memo(CalendarWeekRowBase);
